import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Container, Grid, Input, SpaceBetween, StatusIndicator, Tabs, Link, SegmentedControl } from "@amzn/awsui-components-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { convertToLocalTime, getQuarterForUpload } from "./Utility";
import './styles.scss';
import tieoutLogo from '../../images/tieoutLogo.png'
import aiInsights from '../../images/aiInsights.svg'
import { getDocumentTreeWithTracks } from "./DocumentStoreAPIs";
import TieOutHistoryComponent from "./TieOutHistoryComponent";
import { createPreference, deletePreference, listPreference } from "src/utilities/AppSyncPlusAPIs";
import { deepEqual } from "src/Ateam-BI/utils/generalUtils";
import { v4 as uuidv4 } from 'uuid';
import DownloadTieOutResultsButton from "./DownloadTieOutResultsButton";
import appSettings from './app_settings';



const TieOutPage = ({quarter, associationId,associationType}) => {
    const gridRef = useRef();
    const tabsRef = useRef();
    const userId = useSelector((globalState) => globalState.auth.userId);
    const currentStage = useSelector((globalState) => globalState.auth.currentStage);
    const [selectedDocument, setSelectedDocument] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('document') || '10Q';
    });

    const [entityList, setEntityList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [quickFilterText, setQuickFilterText] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [resultTabs, setResultTabs] = useState([]);
    const [selectedId, setSelectedId] = useState("pre-wb");
    const [showHistory, setShowHistory] = useState(false);
    const [savedFilters, setSavedFilters] = useState({});
    const [haveFiltersChanged, setHaveFiltersChanged] = useState(false);
    const [pendingFilterModel, setPendingFilterModel] = useState(null);
    const [dataRendered, setDataRendered] = useState(false);

    const TIEOUT_FILTER_PREFERENCE_TYPE = `TIEOUT_FILTER`;
    const TIEOUT_ASSOCIATION_ID = `TIEOUT_FILTER_ALIAS#${userId}`;
    const TIEOUT_ASSOCIATION_TYPE = 'TIEOUT_FILTER_ALIAS';

    const defaultColDef = { filter: true, sortable: true, resizable: true, filterParams: { buttons: ['reset'], debounceMs: 200 } };
    const columnDefs = useMemo(() => [
        { field: 'section', headerName: 'Section', width: 200, checkboxSelection: true, headerCheckboxSelection: true },
        { field: 'version', headerName: 'Version', width: 120, valueGetter: () => selectedId === 'pre-wb' ? 'Pre-WB' : 'Post-WB' },
        { field: 'workiva_export_timestamp', headerName: 'Workiva Export Timestamp', width: 100 },
        { field: 'execution_completion', headerName: 'Execution Completion', width: 160 },
        { field: 'status', headerName: 'Status', width: 160, valueGetter: (params) => selectedId === 'pre-wb' ? (params.data?.pre_wb_status || 'Not Run') : (params.data?.post_wb_status || 'Not Run') },
        { field: 'preparer', headerName: 'Preparer', width: 150 },
        { field: 'reviewer', headerName: 'Reviewer', width: 150 },
        { field: 'last_updated', headerName: 'Last Updated', width: 160 },
    ], [selectedId]);

    useEffect(() => {
        if (currentStage) {
            loadView();
        }
    }, [currentStage]);

    useEffect(() => {
        if (userId) {
            loadSavedFilters();
        }
    }, [userId]);

    useEffect(() => {
        if (dataRendered && pendingFilterModel) {
            gridRef.current.api.setFilterModel(pendingFilterModel);
            setPendingFilterModel(null);
        }
    }, [dataRendered, pendingFilterModel]);

    useEffect(() => {
            onFilterTextBoxChanged();
    }, [quickFilterText]);

    function onFilterTextBoxChanged() {
        gridRef?.current?.api?.setGridOption(
        "quickFilterText",
        quickFilterText,
        );
    }

    const loadView = () => {
        fetchEntities(selectedId);
    };

    const loadSavedFilters = () => {
        if (!userId) return;
        listPreference(
            TIEOUT_ASSOCIATION_ID,
            TIEOUT_ASSOCIATION_TYPE,
            TIEOUT_FILTER_PREFERENCE_TYPE,
            function onSuccess(response) {
                const userFilter = response?.data?.listPreference?.items?.[0];
                if (userFilter) {
                    setSavedFilters(userFilter);
                    setHaveFiltersChanged(false);
                    const filterModel = JSON.parse(userFilter.information);
                    setPendingFilterModel(filterModel);
                }
            },
            function onFailure(error) {
                console.error('Error loading saved filters:', error);
            }
        );
    };

    const saveCurrentFilters = async () => {
        if (!gridRef?.current?.api) return;
        const previouslySavedFilters = savedFilters;
        const currentFilterModel = gridRef.current.api.getFilterModel();

        await new Promise((resolve) => {
            const newPreferenceId = uuidv4();
            createPreference(
                newPreferenceId,
                TIEOUT_FILTER_PREFERENCE_TYPE,
                `${userId}'s TieOut Filters`,
                TIEOUT_ASSOCIATION_ID,
                TIEOUT_ASSOCIATION_TYPE,
                userId,
                currentFilterModel,
                null,
                function onSuccess(response) {
                    const newFilter = {
                        preferenceId: newPreferenceId,
                        preferenceType: TIEOUT_FILTER_PREFERENCE_TYPE,
                        label: `${userId}'s TieOut Filters`,
                        information: JSON.stringify(currentFilterModel),
                    };
                    setSavedFilters(newFilter);
                    setHaveFiltersChanged(false);
                    resolve();
                },
                function onFailure(error) {
                    console.error('Error saving filters:', error);
                    resolve();
                }
            );
        });

        if (previouslySavedFilters?.preferenceId) {
            await new Promise((resolve) => {
                deletePreference(
                    previouslySavedFilters.preferenceId,
                    null,
                    () => resolve(),
                    (error) => {
                        console.error('Error deleting old filter:', error);
                        resolve();
                    }
                );
            });
        }
    };

    const onFilterChanged = useCallback(() => {
        if (!gridRef?.current?.api) return;
        const filterModel = gridRef.current.api.getFilterModel();
        const savedFiltersModel = savedFilters?.information ? JSON.parse(savedFilters?.information) : {};
        setHaveFiltersChanged(!deepEqual(savedFiltersModel, filterModel));
    }, [savedFilters]);

    const onFirstDataRendered = useCallback((params) => {
        setDataRendered(true);
    }, []);

    const fetchEntities = async (id) => {
        setIsLoading(true);
        try {
            const version = id === 'pre-wb' ? 'Pre-WB' : 'Post-WB';

            const data = await getDocumentTreeWithTracks({ quarter, associationId, associationType, documentName: selectedDocument });
            const rows = (Array.isArray(data) ? data : []).map(row => {
                const attrs = typeof row.custom_attributes === 'string'
                    ? JSON.parse(row.custom_attributes)
                    : (row.custom_attributes || {});
                const tieout = attrs.TIE_OUT_YN || '';
                const statusField = id === 'pre-wb' ? 'pre_wb_status' : 'post_wb_status';
                const status = row[statusField] || 'Not Run';

                const preparers = typeof row.preparers === 'string' ? JSON.parse(row.preparers) : (row.preparers || []);
                const reviewers = typeof row.reviewers === 'string' ? JSON.parse(row.reviewers) : (row.reviewers || []);

                return {
                    ...row,
                    section: row.title || '',
                    version,
                    preparer: preparers.map(p => p.alias).join(', '),
                    reviewer: reviewers.map(r => r.alias).join(', '),
                    workiva_export_timestamp: row.workiva_export_timestamp || '',
                    execution_completion: row.execution_completion || '',
                    status,
                    last_updated: row.last_updated || '',
                    tieout,
                };
            });
            // Only show sections that have tie-out enabled (TIE_OUT_YN = 'Yes') or show all if none are marked
            const tieoutRows = rows.filter(r => r.tieout === 'Yes');
            setEntityList(tieoutRows.length > 0 ? tieoutRows : rows);
        } catch (e) {
            console.error('Failed to fetch tieout data', e);
            setEntityList([]);
        } finally {
            setIsLoading(false);
        }
    };

    const onSelectionChanged = () => {
        const selectedRows = gridRef?.current?.api?.getSelectedRows();
        setSelectedRows(selectedRows);
    };

    const clearResultsSection = () => {
        setResultTabs([]);
    };

    const viewResults = (selectedRows) => {
        let tabs = [];
        for (let selectedRow of selectedRows) {
        tabs.push({
            label: selectedRow.section || 'Result',
            id: selectedRow.section,
            dismissible: true,
            dismissLabel: "Dismiss",
            onDismiss: () => {
            setResultTabs(prevTabs => prevTabs.filter(tab => tab.id !== selectedRow.section));
            },
            content: (
                <Box padding="l">
                    <SpaceBetween direction="vertical" size="m">
                        <Box float="right">
                            <DownloadTieOutResultsButton data={selectedRow} />
                        </Box>
                        <StatusIndicator type="info">
                            Tie-out results for {selectedRow.section} will appear here.
                        </StatusIndicator>
                    </SpaceBetween>
                </Box>
            )
        });
        }
        setResultTabs(tabs);
        setTimeout(() => {
        tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    if (showHistory) {
        return (
            <Box margin="l">
                <TieOutHistoryComponent onBack={() => setShowHistory(false)} />
            </Box>
        );
    }

    return (
        <Box margin="l">
        <SpaceBetween direction="vertical">
            <Grid
            gridDefinition={[
                { colspan: { default: 3, xxs: 3 } },
                { colspan: { default: 9, xxs: 9 } }
            ]}
            >
            <div className="grid-column-wrapper">
                <Container>
                <SpaceBetween direction="vertical" size="l">
                    <div className="left-container"> <img className="icon" src={tieoutLogo}></img></div>
                    <Box variant="strong">Hello, {userId}!</Box>
                    <Box variant="p">I am your friendly AI financial statement reviewer. I catch potential issues before Reviewer1(R1) review</Box>
                    <Box variant="p">The source of the Financial statements is the Workiva bulk export to S3. Results will be available within 2-3 mins from triggering the bulk export.</Box>
                    <Box>
                    <Box>
                        <Box variant="strong">Let's get started:</Box>
                        <Box variant="p">
                            <ol>
                            <li>Select one or multiple 10K/10Q sections</li>
                            <li>Click view results</li>
                            </ol>
                        </Box>
                    </Box>
                    </Box>
                    <Box variant="p">View past reviews in History</Box>
                </SpaceBetween>
                </Container>
            </div>

            <div className="grid-column-wrapper">
                <div className="right-column-content">
                <div className="search-buttons-row">
                    <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
                    <Input
                        onChange={({ detail }) => setQuickFilterText(detail.value)}
                        value={quickFilterText}
                        expandToViewport={true}
                        placeholder='Type to search'
                    />
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="s">
                        {selectedRows?.length <= 0 && resultTabs?.length > 0 && (
                            <Button onClick={() => clearResultsSection()} variant="primary">Clear results</Button>
                        )}
                        <Button onClick={() => viewResults(selectedRows)} disabled={!selectedRows || selectedRows?.length <= 0} variant="primary">View results</Button>
                        <Button onClick={() => setShowHistory(true)} variant="normal">History</Button>
                        <Button variant="normal"
                            onClick={() => {
                                if (haveFiltersChanged) {
                                    saveCurrentFilters();
                                } else {
                                    gridRef?.current?.api?.setFilterModel(null);
                                    setHaveFiltersChanged(false);
                                }
                            }}
                        >
                            {haveFiltersChanged ? 'Save filters' : 'Clear filters'}
                        </Button>
                        </SpaceBetween>
                    </Box>
                    </Grid>
                </div>
                <Box>
                    <SegmentedControl
                        selectedId={selectedId}
                        onChange={({ detail }) =>
                            setSelectedId(detail.selectedId)
                        }
                        label="WB Control Select"
                        options={[
                            { text: "Pre-WB", id: "pre-wb" },
                            { text: "Post-WB", id: "post-wb" },
                        ]}
                    />
                </Box>
                <div className="ag-grid-wrapper">
                    <div className="ag-theme-alpine">
                    <AgGridReact
                        ref={gridRef}
                        rowData={entityList}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        enableRangeSelection={true}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        onSelectionChanged={onSelectionChanged}
                        animateRows={true}
                        pagination={true}
                        paginationPageSize={10}
                        paginationPageSizeSelector={[10, 50, 100]}
                        loading={isLoading}
                        onFirstDataRendered={onFirstDataRendered}
                        onFilterChanged={onFilterChanged}
                    />
                    </div>
                </div>
                </div>
            </div>
            </Grid>

            {
                resultTabs?.length > 0 && (
                    <Box>
                        <div className="tieout-result-title-wrapper">
                            <SpaceBetween>
                                <Box variant="h4">
                                    <SpaceBetween size="s" direction="horizontal">
                                        <img
                                            className="ai-button"
                                            src={aiInsights}
                                        />
                                            Results Generated by AI
                                    </SpaceBetween>
                                </Box>
                                <Box>
                                    <i>
                                        AI has limitations and may produce omissions or errors. Please review all important information.
                                    </i>
                                </Box>
                            </SpaceBetween>
                        </div>
                        <Container>
                            <div ref={tabsRef}>
                                <SpaceBetween direction="vertical" size="m">
                                    <Tabs tabs={resultTabs} />
                                </SpaceBetween>
                            </div>
                        </Container>
                    </Box>
                )
            }
        </SpaceBetween>
        </Box>
    );
};

export default TieOutPage;
